import { Box, Popover, Portal, useDisclosure } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FocusLock from 'react-focus-lock';
import {
  QUOTE_CONTRACT_LENGTHS,
  QUOTE_CUSTOM_CONTRACT_LENGTHS,
} from '../../constants/quotes';
import {
  MButton,
  MCustomNumberInput,
  MFlex,
  MText,
  MVStack,
  RadioStyleButtonGroup,
} from '../Monetize';
import { FOCUS_WITHOUT_SCROLLING } from '../Monetize/Overlays';

interface IQuoteContractLengthPopover {
  isCustomContractLength: boolean;
  contractLength: number;
  handleValueUpdate: (value: number) => void;
}

/**
 * This component manage the popup form state and submission for both- Whole Years or Any Months
 *
 * if isCustomContractLength is true means it's Any Months otherwise Whole Years
 */

const customContractOptions = QUOTE_CUSTOM_CONTRACT_LENGTHS.map((value) => ({
  label: `${value}`,
  value: `${value}`,
}));
const contractLengthOptions = QUOTE_CONTRACT_LENGTHS.map((value) => ({
  label: `${value}`,
  value: `${value}`,
}));

export const ContractLengthPopover = ({
  isCustomContractLength,
  contractLength,
  handleValueUpdate,
}: IQuoteContractLengthPopover) => {
  const { onOpen, onClose, open } = useDisclosure();

  const [inputValue, setInputValue] = useState<number>(contractLength);

  useEffect(() => {
    handleValueUpdate(inputValue);
  }, [contractLength, handleValueUpdate, inputValue]);

  const handleOnOpen = () => {
    onOpen();
  };

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      restoreFocus
      size={'smaller'}
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
      }}
    >
      <MFlex minH={8} align="center">
        <Popover.Trigger asChild>
          <MButton
            size="sm"
            padding={1}
            variant="tertiary"
            onClick={handleOnOpen}
          >
            {inputValue}
          </MButton>
        </Popover.Trigger>
      </MFlex>

      <Portal>
        <Box zIndex="popover" w="full" h="full">
          <Popover.Positioner>
            <Popover.Content data-testid="custom-contractLength-popover-content">
              <Popover.Body>
                <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
                  <MText fontWeight={'semibold'} color="tPurple.dark">
                    Number of Months
                  </MText>
                  <MVStack
                    gap={2}
                    fontWeight="semibold"
                    alignItems="flex-start"
                    mt={2}
                  >
                    <MCustomNumberInput
                      inputMode="numeric"
                      value={inputValue}
                      width="full"
                      onChange={(
                        valueAsString: string,
                        valueAsNumber: number,
                      ) => {
                        setInputValue(valueAsNumber);
                      }}
                    />

                    <RadioStyleButtonGroup
                      name="contractLength"
                      options={
                        isCustomContractLength
                          ? customContractOptions
                          : contractLengthOptions
                      }
                      value={`${inputValue}`}
                      defaultValue={`${inputValue}`}
                      onChange={(value: string) => {
                        setInputValue(Number(value));
                      }}
                    />
                  </MVStack>
                </FocusLock>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Box>
      </Portal>
    </Popover.Root>
  );
};
